import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { App as AntApp, ConfigProvider, Spin } from 'antd';
import { useAuthStore } from '@/shared/auth/auth-store';
import { useBootstrapAuth } from '@/shared/auth/use-bootstrap-auth';
import { AdminLayout } from '@/shared/layout/admin-layout';
import { LoginPage } from '@/features/auth/pages/login-page';
import { ProjectsListPage } from '@/features/projects/pages/projects-list-page';
import { ProjectDetailPage } from '@/features/projects/pages/project-detail-page';
import { CustomersListPage } from '@/features/customers/pages/customers-list-page';
import { CustomerDetailPage } from '@/features/customers/pages/customer-detail-page';
import { ContractsListPage } from '@/features/contracts/pages/contracts-list-page';
import { ContractCreatePage } from '@/features/contracts/pages/contract-create-page';
import { ContractDetailPage } from '@/features/contracts/pages/contract-detail-page';
import { PaymentsListPage } from '@/features/payments/pages/payments-list-page';
import { PaymentCreatePage } from '@/features/payments/pages/payment-create-page';
import { PaymentDetailPage } from '@/features/payments/pages/payment-detail-page';
import { BankAccountsPage } from '@/features/payments/pages/bank-accounts-page';
import { SmsHubPage } from '@/features/sms/pages/sms-hub-page';
import { DashboardPage } from '@/features/reports/pages/dashboard-page';
import { DocumentTemplatesPage } from '@/features/document-templates/pages/document-templates-page';
import { UsersPage } from '@/features/users/pages/users-page';
import { OwnerDashboardPage } from '@/features/owner/pages/owner-dashboard-page';
import { KassaPage } from '@/features/cash/pages/kassa-page';
import { CounterpartiesPage } from '@/features/cash/pages/counterparties-page';
import { CashCategoriesPage } from '@/features/cash/pages/cash-categories-page';
import { SettingsPage } from '@/features/settings/pages/settings-page';
import { useAntdLocale } from '@/shared/i18n/use-antd-locale';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const themeTokens = {
  token: {
    colorPrimary: '#16a34a',
    borderRadius: 6,
    fontFamily: "Inter, system-ui, -apple-system, 'Segoe UI', sans-serif",
  },
};

function FullPageSpinner() {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#fff',
      }}
    >
      <Spin size="large" />
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const bootstrapped = useAuthStore((s) => s.bootstrapped);
  if (!bootstrapped) return <FullPageSpinner />;
  if (!accessToken) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AuthBootstrapGate({ children }: { children: React.ReactNode }) {
  useBootstrapAuth();
  return <>{children}</>;
}

function LoginGate() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const bootstrapped = useAuthStore((s) => s.bootstrapped);
  if (!bootstrapped) return <FullPageSpinner />;
  if (accessToken) return <Navigate to="/dashboard" replace />;
  return <LoginPage />;
}

export function App() {
  const locale = useAntdLocale();

  return (
    <ConfigProvider theme={themeTokens} locale={locale}>
      <AntApp>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthBootstrapGate>
            <Routes>
              <Route path="/login" element={<LoginGate />} />
              <Route
                element={
                  <RequireAuth>
                    <AdminLayout />
                  </RequireAuth>
                }
              >
                <Route path="/projects" element={<ProjectsListPage />} />
                <Route path="/projects/:id" element={<ProjectDetailPage />} />
                <Route path="/customers" element={<CustomersListPage />} />
                <Route path="/customers/:id" element={<CustomerDetailPage />} />
                <Route path="/contracts" element={<ContractsListPage />} />
                <Route path="/contracts/new" element={<ContractCreatePage />} />
                <Route path="/contracts/:id" element={<ContractDetailPage />} />
                <Route path="/payments" element={<PaymentsListPage />} />
                <Route path="/payments/new" element={<PaymentCreatePage />} />
                <Route path="/payments/:id" element={<PaymentDetailPage />} />
                <Route path="/bank-accounts" element={<BankAccountsPage />} />
                <Route path="/sms" element={<SmsHubPage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/owner" element={<OwnerDashboardPage />} />
                <Route path="/document-templates" element={<DocumentTemplatesPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/kassa" element={<KassaPage />} />
                <Route path="/kassa/:id" element={<KassaPage />} />
                <Route path="/counterparties" element={<CounterpartiesPage />} />
                <Route path="/cash-categories" element={<CashCategoriesPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Route>
            </Routes>
            </AuthBootstrapGate>
          </BrowserRouter>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </AntApp>
    </ConfigProvider>
  );
}
